// App Launcher dropdown for the sidebar header (T-033, plan §5.3,
// spec §4.3).
//
// Click the small grid button at the top-left of the sidebar to open
// a 2-item dropdown: Home, System Settings. Click-outside dismisses
// (resolved Q3 = A in the plan's open items).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, LayoutGrid, Settings } from 'lucide-react';

interface LauncherItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  href: string;
}

const ITEMS: LauncherItem[] = [
  { key: 'home', label: 'Home', icon: Home, href: '/' },
  { key: 'settings', label: 'System Settings', icon: Settings, href: '/settings' },
];

export function AppLauncherDropdown() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function pick(item: LauncherItem) {
    setOpen(false);
    navigate(item.href);
  }

  return (
    <div ref={containerRef} className="relative" data-testid="app-launcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open app launcher"
        className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
        data-testid="app-launcher-trigger"
      >
        <LayoutGrid size={18} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-50"
          data-testid="app-launcher-menu"
        >
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                onClick={() => pick(item)}
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                data-testid={`app-launcher-item-${item.key}`}
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
